import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Commit } from '../types/api';

interface UIStore {
  // === FLAT MODAL STATE (each modal independent) ===
  commitEditorOpen: boolean;
  commitEditorCommit: Commit | null;

  deleteConfirmOpen: boolean;
  deleteConfirmCommit: Commit | null;

  linkedInShareOpen: boolean;
  linkedInShareCommit: Commit | null;

  settingsOpen: boolean;
  editProfileOpen: boolean;
  branchesOpen: boolean;

  // === OPTIMISTIC DELETE ===
  deletedCommitIds: Set<string>;

  // === SUBSCRIPTION-BASED COMMUNICATION ===
  feedSubscribers: Set<() => void>;
  profileSubscribers: Set<() => void>;

  // === MODAL ACTIONS ===
  openCommitEditor: (commit?: Commit) => void;
  closeCommitEditor: () => void;
  openDeleteConfirm: (commit: Commit) => void;
  closeDeleteConfirm: () => void;
  openLinkedInShare: (commit: Commit) => void;
  closeLinkedInShare: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  openEditProfile: () => void;
  closeEditProfile: () => void;
  openBranches: () => void;
  closeBranches: () => void;

  // === SUBSCRIPTION ACTIONS ===
  subscribeFeed: (callback: () => void) => () => void;
  subscribeProfile: (callback: () => void) => () => void;
  notifyFeedSubscribers: () => void;
  notifyProfileSubscribers: () => void;

  // === OPTIMISTIC DELETE ACTIONS ===
  markCommitDeleted: (commitId: string) => void;
  unmarkCommitDeleted: (commitId: string) => void;
}

export const useUIStore = create<UIStore>()(
  devtools(
    (set, get) => ({
      // Flat modal state
      commitEditorOpen: false,
      commitEditorCommit: null,
      deleteConfirmOpen: false,
      deleteConfirmCommit: null,
      linkedInShareOpen: false,
      linkedInShareCommit: null,
      settingsOpen: false,
      editProfileOpen: false,
      branchesOpen: false,

      // Optimistic delete
      deletedCommitIds: new Set(),

      // Subscribers
      feedSubscribers: new Set(),
      profileSubscribers: new Set(),

      // Modal actions - only update affected properties
      openCommitEditor: (commit) => set({
        commitEditorOpen: true,
        commitEditorCommit: commit ?? null,
      }),
      closeCommitEditor: () => set({
        commitEditorOpen: false,
        commitEditorCommit: null,
      }),
      openDeleteConfirm: (commit) => set({
        deleteConfirmOpen: true,
        deleteConfirmCommit: commit,
      }),
      closeDeleteConfirm: () => set({
        deleteConfirmOpen: false,
        deleteConfirmCommit: null,
      }),
      openLinkedInShare: (commit) => set({
        linkedInShareOpen: true,
        linkedInShareCommit: commit,
      }),
      closeLinkedInShare: () => set({
        linkedInShareOpen: false,
        linkedInShareCommit: null,
      }),
      openSettings: () => set({ settingsOpen: true }),
      closeSettings: () => set({ settingsOpen: false }),
      openEditProfile: () => set({ editProfileOpen: true }),
      closeEditProfile: () => set({ editProfileOpen: false }),
      openBranches: () => set({ branchesOpen: true }),
      closeBranches: () => set({ branchesOpen: false }),

      // Subscription actions
      subscribeFeed: (callback) => {
        const subscribers = new Set(get().feedSubscribers);
        subscribers.add(callback);
        set({ feedSubscribers: subscribers });
        // Return unsubscribe function
        return () => {
          const current = new Set(get().feedSubscribers);
          current.delete(callback);
          set({ feedSubscribers: current });
        };
      },
      subscribeProfile: (callback) => {
        const subscribers = new Set(get().profileSubscribers);
        subscribers.add(callback);
        set({ profileSubscribers: subscribers });
        return () => {
          const current = new Set(get().profileSubscribers);
          current.delete(callback);
          set({ profileSubscribers: current });
        };
      },
      notifyFeedSubscribers: () => {
        get().feedSubscribers.forEach((cb) => cb());
      },
      notifyProfileSubscribers: () => {
        get().profileSubscribers.forEach((cb) => cb());
      },

      // Optimistic delete actions
      markCommitDeleted: (commitId) => {
        const updated = new Set(get().deletedCommitIds);
        updated.add(commitId);
        set({ deletedCommitIds: updated });
      },
      unmarkCommitDeleted: (commitId) => {
        const updated = new Set(get().deletedCommitIds);
        updated.delete(commitId);
        set({ deletedCommitIds: updated });
      },
    }),
    { name: 'ui-store' }
  )
);
